import { formatRelative, humanDuration } from "@/lib/format"

import type { HealthReport } from "./types"

/**
 * The page's verdict layer: five subsystem statuses derived client-side from
 * the report, nothing else. Levels rank fail > attention > unknown > ok
 * within a section, but the strip renders sections in fixed order — severity
 * moves glyph and ink, never position.
 *
 * "unknown" is deliberately quieter than "attention": it means the report
 * cannot attest either way (a DKIM selector we don't hold, GoodJob tables we
 * cannot read, a system that has never sent). Rendering absence-of-evidence
 * as amber would leave the strip amber forever on some environments, and
 * permanent amber teaches operators to ignore amber.
 */
export type SectionLevel = "ok" | "attention" | "fail" | "unknown"

export interface SectionStatus {
  id: "config" | "domains" | "pipeline" | "webhooks" | "queue"
  label: string
  level: SectionLevel
  /** One pre-formatted line: the single most useful fact for this section. */
  fact: string
  anchor: string
}

/** Clears normal queue latency plus Postmark's webhook retry backoff. */
export const STUCK_ATTENTION_SECONDS = 600
/** A live row unconfirmed this long means mail is not moving. */
export const STUCK_FAIL_SECONDS = 1800

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`

export function deriveSectionStatuses(report: HealthReport): SectionStatus[] {
  return [
    config(report),
    domains(report),
    pipeline(report),
    webhooks(report),
    queue(report),
  ]
}

function config({ transport, credentials }: HealthReport): SectionStatus {
  const base = { id: "config" as const, label: "Config", anchor: "#config" }
  // Inapplicable credentials never count as missing: production holds no
  // mailtrap keys and must not sit amber over them forever.
  const missing = credentials.filter(
    (credential) => credential.applicable && !credential.present,
  )

  if (!transport.matches_matrix) {
    return {
      ...base,
      level: "fail",
      fact: `ActionMailer is ${transport.delivery_method}, matrix wants ${transport.provider}`,
    }
  }
  // Missing credentials mirror the boot check: a warning, never a failure.
  // Which key matters in which environment lives in the `breaks` prose below.
  if (missing.length > 0) {
    return {
      ...base,
      level: "attention",
      fact: `${count(missing.length, "credential")} missing`,
    }
  }
  return {
    ...base,
    level: "ok",
    fact: `${transport.provider} via ${transport.provider_source}`,
  }
}

function domains({ deliverability }: HealthReport): SectionStatus {
  const base = { id: "domains" as const, label: "Domains", anchor: "#domains" }
  const records = ["spf", "dkim", "dmarc"] as const

  const failing = deliverability.domains.flatMap((domain) =>
    domain.available
      ? records
          .filter((record) => domain[record]?.ok === false)
          .map((record) => ({
            record: record.toUpperCase(),
            domain: domain.domain,
          }))
      : [],
  )
  if (failing.length === 1) {
    return {
      ...base,
      level: "fail",
      fact: `${failing[0].record} failing on ${failing[0].domain}`,
    }
  }
  if (failing.length > 1) {
    return { ...base, level: "fail", fact: `${failing.length} checks failing` }
  }

  const unavailable = deliverability.domains.find((domain) => !domain.available)
  if (unavailable) {
    return {
      ...base,
      level: "attention",
      fact: `DNS lookup failed for ${unavailable.domain}`,
    }
  }

  for (const domain of deliverability.domains) {
    for (const record of records) {
      if (domain[record]?.ok === null) {
        return {
          ...base,
          level: "unknown",
          fact: `${record.toUpperCase()} on ${domain.domain} cannot be verified`,
        }
      }
    }
  }

  return {
    ...base,
    level: "ok",
    fact: `SPF, DKIM, DMARC pass on ${count(deliverability.domains.length, "domain")}`,
  }
}

function pipeline({ ledger, webhooks }: HealthReport): SectionStatus {
  const base = {
    id: "pipeline" as const,
    label: "Pipeline",
    anchor: "#movement",
  }
  const stuck = ledger.awaiting_webhook
  const age = stuck.oldest_age_seconds

  // Where webhooks cannot arrive (development), a live row can never leave
  // Sent, so its age escalating to red would be a permanent false alarm.
  if (!webhooks.expected) {
    if (ledger.total === 0) {
      return { ...base, level: "unknown", fact: "no mail recorded yet" }
    }
    return {
      ...base,
      level: "ok",
      fact:
        stuck.count > 0
          ? `${count(stuck.count, "live row")} at Sent — no webhook will move them`
          : "all rows settled",
    }
  }

  if (age !== null && age >= STUCK_FAIL_SECONDS) {
    return {
      ...base,
      level: "fail",
      fact: `oldest live row waiting ${humanDuration(age)}`,
    }
  }
  if (age !== null && age >= STUCK_ATTENTION_SECONDS) {
    return {
      ...base,
      level: "attention",
      fact: `oldest live row waiting ${humanDuration(age)}`,
    }
  }
  if (ledger.total === 0) {
    return { ...base, level: "unknown", fact: "no mail recorded yet" }
  }
  if (stuck.count > 0) {
    // Briefly-Sent is definitional for live mail; a young age is health.
    return {
      ...base,
      level: "ok",
      fact: `${count(stuck.count, "row")} awaiting a webhook · oldest ${humanDuration(age)}`,
    }
  }
  return { ...base, level: "ok", fact: "all rows settled" }
}

function webhooks({
  webhooks,
  ledger,
  transport,
}: HealthReport): SectionStatus {
  const base = {
    id: "webhooks" as const,
    label: "Webhooks",
    anchor: "#webhooks",
  }

  // Not silence, geography: Postmark cannot POST to a host with no public
  // URL, so nothing below can attest either way.
  if (!webhooks.expected) {
    return {
      ...base,
      level: "unknown",
      fact: `Postmark cannot reach ${transport.rails_env} — no events will arrive`,
    }
  }

  // Server-computed conjunction: no event has EVER arrived while live rows
  // sit waiting for one. Points at the endpoint, not the pipeline.
  if (webhooks.never_received) {
    return {
      ...base,
      level: "fail",
      fact: `none ever received · ${count(ledger.awaiting_webhook.count, "live row")} waiting`,
    }
  }
  if (webhooks.unmatched_24h > 0) {
    return {
      ...base,
      level: "attention",
      fact: `${webhooks.unmatched_24h} unmatched in 24 h`,
    }
  }
  if (webhooks.total === 0) {
    return { ...base, level: "unknown", fact: "none received yet" }
  }
  return {
    ...base,
    level: "ok",
    fact: `last ${formatRelative(webhooks.last_received_at)} · ${webhooks.count_24h} in 24 h`,
  }
}

function queue({ queue }: HealthReport): SectionStatus {
  const base = { id: "queue" as const, label: "Queue", anchor: "#queue" }

  // The zeros that come back with available:false are placeholders, not
  // measurements — assert neither health nor failure from them.
  if (!queue.available) {
    return {
      ...base,
      level: "unknown",
      fact: "GoodJob unreadable — see the log",
    }
  }
  if (queue.discarded_24h > 0) {
    return {
      ...base,
      level: "fail",
      fact: `${count(queue.discarded_24h, "delivery job")} discarded in 24 h`,
    }
  }
  return {
    ...base,
    level: "ok",
    fact: `${queue.queued} queued · ${queue.finished_24h} finished in 24 h`,
  }
}
